import { useEffect, useRef } from 'react'
import { useDataSource } from '../../../../../lib/dataSource'
import type { Task, Section } from '../../../../../lib/db/models'

interface UsePageCrossfadeParams {
  projectId: string | undefined
  project: { id: string } | null | undefined
}

interface UsePageCrossfadeResult {
  tasks: Task[] | undefined
  sections: Section[] | undefined
  pendingRestoreRef: React.MutableRefObject<string | null>
  pendingSelectionRestoreRef: React.MutableRefObject<string | null>
  correctDataReady: boolean
}

export function usePageCrossfade({
  projectId,
  project,
}: UsePageCrossfadeParams): UsePageCrossfadeResult {
  // Seed prev with a sentinel so the very first render also counts as a
  // "change" — that arms the restore path on a fresh ProjectPage mount
  // (e.g. after navigating away to Quick Notes and back).
  const prevProjectIdRef = useRef<string | undefined | null>(undefined)
  const pendingRestoreRef = useRef<string | null>(null)
  const pendingSelectionRestoreRef = useRef<string | null>(null)

  // Detect projectId change during render phase (before stale guards + continuous save).
  if (projectId !== prevProjectIdRef.current) {
    pendingRestoreRef.current = projectId ?? null
    pendingSelectionRestoreRef.current = projectId ?? null
    prevProjectIdRef.current = projectId
  }

  // Reactive reads — auto-update on writes. A live query may keep the
  // previous project's result until the new one resolves; we guard against
  // this by checking projectId on the data.
  //
  // Unchanged tasks and sections keep their reference (see `DataSource`), so
  // downstream memos (`segments`, …) bail and the whole ProjectPage subtree
  // (TaskBoard, DndContext, SortableContext, TaskList) memo-holds instead of
  // re-rendering on each tab switch.
  const source = useDataSource()
  const tasksRaw = source.useTasksByProject(projectId)
  const sectionsRaw = source.useAllSectionsByProject(projectId)

  // Block stale data: if the returned data belongs to a different project,
  // treat it as undefined (renders empty state until correct data arrives).
  const tasks = (!tasksRaw || tasksRaw.length === 0 || tasksRaw[0].projectId === projectId)
    ? tasksRaw : undefined
  const sections = (!sectionsRaw || sectionsRaw.length === 0 || sectionsRaw[0].projectId === projectId)
    ? sectionsRaw : undefined

  // Check if the correct (non-stale) data has arrived.
  const hasCorrectTasks = !tasksRaw || tasksRaw.length === 0 || tasksRaw[0].projectId === projectId
  const hasCorrectSections = !sectionsRaw || sectionsRaw.length === 0 || sectionsRaw[0].projectId === projectId
  const correctDataReady = hasCorrectTasks && hasCorrectSections
    && !!project && project.id === projectId

  // Clear pendingRestoreRef once correct data is available (used by useSelectionCache)
  useEffect(() => {
    if (!correctDataReady) return
    if (pendingRestoreRef.current) {
      pendingRestoreRef.current = null
    }
  }, [correctDataReady])

  return { tasks, sections, pendingRestoreRef, pendingSelectionRestoreRef, correctDataReady }
}
